// FILE: src/components/case-studies/admin/OverviewPanel.js
// Case metadata form with tag picker (curated vocab = suggestions; unknown
// tags are advisory warnings, never blockers) and dirty-state reporting for
// the editor's unsaved-change guard.

import React, { useEffect, useState } from 'react';
import { adminUpsertCaseStudy } from '../../../services/caseStudyService';
import { CASE_STUDY_TAGS } from '../../../config/examCategories';
import { isOverviewDirty, tagWarnings } from './caseAdminUtils';
import { S, T } from './caseAdminStyles';

const APPROACHES = ['', 'predictive', 'agile', 'hybrid', 'mixed'];
const DIFFICULTIES = ['', 'Easy', 'Medium', 'Hard', 'Expert'];
const STATUSES = ['draft', 'review', 'approved', 'archived']; // 'published' only via the publish gate

function toForm(cs) {
  return {
    slug: cs.slug || '', title: cs.title || '', summary: cs.summary || '',
    learner_role: cs.learner_role || '', delivery_approach: cs.delivery_approach || '',
    difficulty: cs.difficulty || '', estimated_minutes: cs.estimated_minutes ?? '',
    source_reference: cs.source_reference || '', author_notes: cs.author_notes || '',
    status: cs.status || 'draft', tags: cs.tags || [],
  };
}

export default function OverviewPanel({ full, onDirtyChange, onSaved }) {
  // `baseline` is the last-loaded OR last-successfully-saved form state.
  // Dirty is derived from it (never from object identity of `full`), so a
  // reload that returns equivalent data — or a save whose reload is still
  // in flight — can never re-engage the unsaved-changes guard incorrectly.
  const [baseline, setBaseline] = useState(() => toForm(full.case));
  const [form, setForm] = useState(baseline);
  const [tagInput, setTagInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const f = toForm(full.case);
    setBaseline(f);
    setForm(f);
  }, [full]);
  const dirty = isOverviewDirty(form, baseline);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const warnings = tagWarnings(form.tags);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function addTag(t) {
    const tag = (t || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (!tag || form.tags.includes(tag)) return;
    setForm({ ...form, tags: [...form.tags, tag] });
    setTagInput('');
  }

  async function save() {
    setBusy(true); setError(null);
    try {
      await adminUpsertCaseStudy({
        id: full.case.id, ...form,
        estimated_minutes: form.estimated_minutes === '' ? null : Number(form.estimated_minutes),
      });
      setBaseline(form); // saved state is the new clean baseline
      await onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const field = (label, key, props = {}, El = 'input') => (
    <div>
      <label style={S.label}>{label}</label>
      <El style={{ ...S.input, ...(El === 'textarea' ? { minHeight: 74, resize: 'vertical' } : {}) }}
        data-testid={`case-overview-${key}`} value={form[key]} onChange={set(key)} {...props} />
    </div>
  );

  return (
    <div style={{ ...S.panel, maxWidth: 860 }} data-testid="case-overview-panel">
      {error && <div style={S.err}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
        {field('Title', 'title')}
        {field('Slug', 'slug')}
        {field('Learner role', 'learner_role')}
        <div>
          <label style={S.label}>Estimated minutes</label>
          <input style={S.input} type="number" min="1" data-testid="case-overview-estimated_minutes"
            value={form.estimated_minutes} onChange={set('estimated_minutes')} />
        </div>
        <div>
          <label style={S.label}>Delivery approach</label>
          <select style={S.input} value={form.delivery_approach} onChange={set('delivery_approach')}>
            {APPROACHES.map((a) => <option key={a} value={a}>{a || '—'}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Difficulty</label>
          <select style={S.input} value={form.difficulty} onChange={set('difficulty')}>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{d || '—'}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Status</label>
          <select style={S.input} data-testid="case-overview-status" value={form.status} onChange={set('status')}
            disabled={full.case.status === 'published'}>
            {(full.case.status === 'published' ? ['published', ...STATUSES] : STATUSES).map((s) =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      {field('Summary', 'summary', {}, 'textarea')}
      {field('Source reference (internal)', 'source_reference')}
      {field('Author notes (internal — never shown to learners)', 'author_notes', {}, 'textarea')}

      <label style={S.label}>Thematic tags (advisory vocabulary)</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }} data-testid="case-overview-tags">
        {form.tags.map((t) => (
          <span key={t} style={S.chip(CASE_STUDY_TAGS.includes(t) ? T.brandSoft : T.warnSoft,
                                      CASE_STUDY_TAGS.includes(t) ? T.brand : '#8a6116')}>
            {t}{' '}
            <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 800 }}
              aria-label={`Remove tag ${t}`}
              onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}>×</button>
          </span>
        ))}
      </div>
      <input style={S.input} placeholder="Add a tag and press Enter" value={tagInput} list="case-tag-vocab"
        data-testid="case-overview-tag-input"
        onChange={(e) => setTagInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }} />
      <datalist id="case-tag-vocab">
        {CASE_STUDY_TAGS.map((t) => <option key={t} value={t} />)}
      </datalist>
      {warnings.map((w) => (
        <div key={w} style={{ color: '#8a6116', background: T.warnSoft, borderRadius: 6, padding: '5px 9px', fontSize: '0.76rem', marginTop: 6 }}>
          ⚠ {w}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
        <button style={S.btnPrimary} data-testid="case-overview-save" disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save overview'}
        </button>
        {dirty && <span style={{ ...S.sub, color: T.warn }} data-testid="case-overview-dirty">Unsaved changes</span>}
      </div>
    </div>
  );
}
